import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-geometryutil';
import startIconUrl from '../assets/images/start.png';
import finishIconUrl from '../assets/images/finish.png';
import { API_config } from '../config/apiConfig';

const API_URL = API_config.dataApiUrl;
const POPUP_MARGIN = 10;

function getScaledIcons(map, offsetX = 18) {
  const zoom = map.getZoom();
  const baseline = 20;
  const scale = zoom / baseline;
  return {
    startIcon: L.icon({
      iconUrl: startIconUrl,
      iconSize: [30 * scale, 30 * scale],
      iconAnchor: [(10 + offsetX) * scale, 10 * scale],
    }),
    finishIcon: L.icon({
      iconUrl: finishIconUrl,
      iconSize: [30 * scale, 30 * scale],
      iconAnchor: [(10 + offsetX) * scale, 10 * scale],
    }),
  };
}

// API zwraca [lon,lat]; my potrzebujemy [lat,lon]
function normalizeCoords(rawCoords) {
  return (rawCoords || [])
    .map(([a, b]) => [parseFloat(b), parseFloat(a)])
    .filter(([lat, lon]) =>
      Number.isFinite(lat) && Number.isFinite(lon) &&
      Math.abs(lat) <= 90 && Math.abs(lon) <= 180
    );
}

function buildRouteIndexFromLatLngs(latlngs) {
  const cum = [0];
  for (let i = 1; i < latlngs.length; i++) {
    cum[i] = cum[i - 1] + latlngs[i - 1].distanceTo(latlngs[i]);
  }
  return { latlngs, cum, length: cum[cum.length - 1] };
}

// helper to identify unique track for a stage
function getStageKey(st = {}) {
  return st.stage_typ || st.template_road_id || st.id;
}

/**
 * Props:
 * - setSectionsData: fn(sections[])  -> do panelu (akordeon)
 * - selectedSection: string|null      -> aktualnie wybrana sekcja
 * - selectedStageId: number|null      -> podświetlany odcinek
 * - setOdcinkiData:  fn(odcinki[])    -> struktury dla SOS/StageProgress
 */
function GPXLoaderSQL({ setSectionsData, selectedSection, selectedStageId, setOdcinkiData }) {
  const map = useMap();
  const sectionsRef = useRef([]); // dane sekcji z API
  const sectionPolysRef = useRef({}); // id -> {polyline, routeIndex, kind}
  const markersRef = useRef({ start: null, finish: null });
  const popupRef = useRef(L.popup());

  // 1) pobierz sekcje do panelu
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}?type=stages_grouped`, {
          headers: {
            'Authorization': API_config.bearerAuthHeader
          }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        sectionsRef.current = (data.sections || []).map((sec) => ({
          ...sec,
          os: (sec.os || [])
            .map((o) => ({ ...o }))
            .sort((a, b) => (a.run_no ?? 0) - (b.run_no ?? 0)),
        }));
        // panel potrzebuje tablicy stages: tylko odcinki OS
        const normalized = sectionsRef.current.map((sec) => ({
          section: sec.section,
          stages: [...(sec.os || [])],
        }));
        setSectionsData?.(normalized);
      } catch (e) {
        console.error('Failed to fetch grouped stages:', e);
        setSectionsData?.([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sprzątanie poprzednich warstw
  const clear = () => {
    Object.values(sectionPolysRef.current).forEach(({ polyline }) => map.removeLayer(polyline));
    sectionPolysRef.current = {};
    if (markersRef.current.start) { map.removeLayer(markersRef.current.start); markersRef.current.start = null; }
    if (markersRef.current.finish) { map.removeLayer(markersRef.current.finish); markersRef.current.finish = null; }
    map.closePopup();
    setOdcinkiData?.([]);
  };

  // 2) rysuj wybraną sekcję (OS + dojazdy)
  useEffect(() => {
    clear();
    if (!selectedSection) return;
    const sec = sectionsRef.current.find((s) => s.section === selectedSection);
    if (!sec) return;

    const stageList = [...(sec.os || []), ...(sec.liaisons || [])];

    (async () => {
      try {
        // prepare unique list for GPX fetching
        const keyMap = new Map();
        const fetchList = [];
        stageList.forEach((st) => {
          const key = getStageKey(st);
          if (!keyMap.has(key)) {
            keyMap.set(key, st);
            fetchList.push(st);
          }
        });

        const results = await Promise.all(
          fetchList.map((st) =>
            fetch(`${API_URL}?type=stage&id=${st.id}`, {
              headers: {
                'Authorization': API_config.bearerAuthHeader
              }
            }).then((r) => (r.ok ? r.json() : null))
          )
        );

        const basePolys = {};
        const bounds = L.latLngBounds([]);

        results.forEach((data, idx) => {
          if (!data) return;
          const meta = fetchList[idx];
          const coords = normalizeCoords(data.coords);
          if (coords.length < 2) return;
          const latlngs = coords.map(([la, lo]) => L.latLng(la, lo));
          const color = meta.kind === 'LIAISON' ? 'blue' : 'red';
          const polyline = L.polyline(latlngs, { color, opacity: 0.7, weight: 3 }).addTo(map);
          bounds.extend(polyline.getBounds());
          const routeIndex = buildRouteIndexFromLatLngs(latlngs);
          if (meta.kind !== 'LIAISON') {
            polyline.on('mousemove', function (e) {
              const latlng = e.latlng;
              const latlngs = routeIndex.latlngs;
              let closestPoint = null;
              let minDistance = Infinity;
              let segIdx = -1;
              for (let i = 1; i < latlngs.length; i++) {
                const A = latlngs[i - 1];
                const B = latlngs[i];
                const dist = L.GeometryUtil.distanceSegment(map, latlng, A, B);
                if (dist < minDistance) {
                  minDistance = dist;
                  closestPoint = L.GeometryUtil.closestOnSegment(map, latlng, A, B);
                  segIdx = i;
                }
              }
              if (closestPoint && minDistance <= POPUP_MARGIN) {
                const distanceFromStart = routeIndex.cum[segIdx - 1] + latlngs[segIdx - 1].distanceTo(closestPoint);
                const km = (distanceFromStart / 1000).toFixed(2);
                popupRef.current
                  .setLatLng(latlng)
                  .setContent(`Dystans: ${km} km`)
                  .openOn(map);
              }
            });
            polyline.on('mouseout', () => map.closePopup());
          }
          basePolys[getStageKey(meta)] = { polyline, routeIndex, kind: meta.kind };
        });

        // map all stages (including duplicates) to polylines
        const polysMap = {};
        const odcinki = [];
        stageList.forEach((st) => {
          const base = basePolys[getStageKey(st)];
          if (!base) return;
          polysMap[st.id] = base;
          if (st.kind !== 'LIAISON') {
            odcinki.push({
              id: st.id,
              name: st.name,
              kind: st.kind,
              bounds: base.polyline.getBounds(),
              polyline: base.polyline,
              routeIndex: base.routeIndex,
            });
          }
        });

        sectionPolysRef.current = polysMap;
        setOdcinkiData?.(odcinki);
        if (bounds.isValid()) map.fitBounds(bounds);

        highlightStage(selectedStageId);
      } catch (e) {
        console.error('Failed to load section stages:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection]);

  // podświetlanie i przybliżenie wybranego odcinka
  const highlightStage = (id) => {
    Object.values(sectionPolysRef.current).forEach(({ polyline, kind }) => {
      const baseColor = kind === 'LIAISON' ? 'blue' : 'red';
      polyline.setStyle({ color: baseColor, opacity: 0.7, weight: 3 });
    });

    if (!id || !sectionPolysRef.current[id]) return;

    const { polyline, routeIndex } = sectionPolysRef.current[id];
    polyline.setStyle({ color: 'red', opacity: 1, weight: 5 });
    map.fitBounds(polyline.getBounds());

    // znaczniki start/finish tylko dla wybranego odcinka
    if (markersRef.current.start) { map.removeLayer(markersRef.current.start); markersRef.current.start = null; }
    if (markersRef.current.finish) { map.removeLayer(markersRef.current.finish); markersRef.current.finish = null; }
    const { startIcon, finishIcon } = getScaledIcons(map, 5);
    const latlngs = routeIndex.latlngs;
    markersRef.current.start = L.marker(latlngs[0], { icon: startIcon }).addTo(map);
    markersRef.current.finish = L.marker(latlngs[latlngs.length - 1], { icon: finishIcon }).addTo(map);
  };

  useEffect(() => {
    highlightStage(selectedStageId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStageId]);

  // skalowanie ikon przy zoomie
  useEffect(() => {
    const updateMarkers = () => {
      if (!markersRef.current.start || !markersRef.current.finish) return;
      const { startIcon, finishIcon } = getScaledIcons(map, 5);
      markersRef.current.start.setIcon(startIcon);
      markersRef.current.finish.setIcon(finishIcon);
    };
    map.on('zoomend', updateMarkers);
    return () => map.off('zoomend', updateMarkers);
  }, [map]);

  // sprzątanie przy odmontowaniu
  useEffect(() => () => clear(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

export default GPXLoaderSQL;

